// CHANGE: Removed all debug console statements for production
// User import modal for CSV-based bulk user creation
// Handles file upload, validation, preview, and batch import
// CHANGE: Updated to use readCSVFileWithEncoding for proper accent handling
// REFACTOR: Moved direct supabase.from() sites query to getActiveSites() service function

import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Upload, Download, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import type { Site } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';
import {
  parseCSV,
  downloadCSVTemplate,
  type CSVUserRow,
} from '../../lib/csvImport';
import { createUser } from '../../lib/auth';
import { createSite, getActiveSites } from '../../lib/sites';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { ImportResultModal, type ImportResult } from './ImportResultModal';
import { readCSVFileWithEncoding } from '../../lib/csvEncoding';
import { ProgressBar } from '../ui/ProgressBar';

interface UserImportModalProps {
  sites: Site[];
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'preview' | 'confirm-sites' | 'importing';

export function UserImportModal({ sites, onClose, onImportComplete }: UserImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [csvFile, setCSVFile] = useState<File | null>(null);
  const [parsedUsers, setParsedUsers] = useState<CSVUserRow[]>([]);
  const [newSites, setNewSites] = useState<string[]>([]);
  const [selectedNewSites, setSelectedNewSites] = useState<Set<string>>(new Set());
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, stage: '' });
  const [importResults, setImportResults] = useState<ImportResult[] | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const validUsers = parsedUsers.filter(u => u.isValid);
  const invalidUsers = parsedUsers.filter(u => !u.isValid);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) {
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [importing]);

  const handleCloseAttempt = () => {
    if (step !== 'upload' && !importing) {
      setShowCloseConfirmation(true);
    } else {
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !importing) {
      handleCloseAttempt();
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Le fichier est trop volumineux (max 5MB)', 'error');
      return;
    }

    // Check file type
    if (!file.name.endsWith('.csv')) {
      showToast('Seuls les fichiers CSV sont acceptés', 'error');
      return;
    }

    setCSVFile(file);

    try {
      // Use encoding-aware reader to properly handle accents
      const content = await readCSVFileWithEncoding(file);
      const result = await parseCSV(content, sites);

      if (result.errors.length > 0) {
        setParseErrors(result.errors);
        showToast(result.errors[0], 'error');
        return;
      }

      // Check max users limit
      if (result.users.length > 500) {
        showToast('Le fichier contient trop d\'utilisateurs (max 500)', 'error');
        setParseErrors(['Le fichier contient trop d\'utilisateurs (max 500)']);
        return;
      }

      setParsedUsers(result.users);
      setNewSites(result.newSites);
      setSelectedNewSites(new Set(result.newSites));
      setParseErrors([]);

      if (result.users.length === 0) {
        showToast('Aucun utilisateur valide trouvé dans le fichier', 'error');
        return;
      }

      setStep('preview');
    } catch (error) {
      showToast('Erreur lors de la lecture du fichier CSV', 'error');
      setParseErrors(['Erreur lors de la lecture du fichier. Vérifiez le format.']);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleProceedToSiteConfirmation = () => {
    if (validUsers.length === 0) {
      showToast('Aucun utilisateur valide à importer', 'error');
      return;
    }

    if (newSites.length > 0) {
      setStep('confirm-sites');
    } else {
      handleStartImport();
    }
  };

  const handleStartImport = async () => {
    setStep('importing');
    setImporting(true);

    const results: ImportResult[] = [];
    const createdSiteNames = new Set<string>();

    try {
      // Step 1: Create new sites if any
      if (selectedNewSites.size > 0) {
        setImportProgress({
          current: 0,
          total: selectedNewSites.size,
          stage: 'Création des sites',
        });

        for (const siteName of Array.from(selectedNewSites)) {
          const result = await createSite(siteName);

          if (result.success) {
            createdSiteNames.add(siteName);
          } else {
            showToast(`Erreur lors de la création du site "${siteName}": ${result.error}`, 'error');
          }

          setImportProgress(prev => ({
            ...prev,
            current: prev.current + 1,
          }));
        }

        // Reload sites to get the newly created ones with their IDs
        // In a real app, you'd fetch from the database here
        // For now, we'll continue with the import
      }

      // Step 2: Create users
      const usersToImport = validUsers.filter(user => {
        // Skip users that require sites that weren't created
        const userSites = [user.site1, user.site2, user.site3, user.site4, user.site5]
          .filter(site => site && site.length > 0);

        if (userSites.length > 0) {
          const hasUncreatedSites = userSites.some(
            site => newSites.includes(site) && !selectedNewSites.has(site)
          );
          return !hasUncreatedSites;
        }
        return true;
      });

      setImportProgress({
        current: 0,
        total: usersToImport.length,
        stage: 'Création des utilisateurs',
      });

      const sitesResult = await getActiveSites();
      const updatedSites = sitesResult.success ? sitesResult.data || [] : [];

      const siteMap = new Map(
        updatedSites.map(site => [site.name.toLowerCase(), site.id])
      );

      // Process users in batches of 10
      const BATCH_SIZE = 10;
      for (let i = 0; i < usersToImport.length; i += BATCH_SIZE) {
        const batch = usersToImport.slice(i, i + BATCH_SIZE);

        await Promise.all(
          batch.map(async (user) => {
            try {
              // Map site names to IDs
              const siteNames = [user.site1, user.site2, user.site3, user.site4, user.site5]
                .filter(site => site && site.length > 0);

              const siteIds = siteNames
                .map(name => siteMap.get(name.toLowerCase()))
                .filter(Boolean) as string[];

              const result = await createUser(
                user.generatedUsername,
                user.first_name,
                user.last_name,
                '', // Password will be auto-generated
                user.role as any,
                siteIds
              );

              if (result.success && result.tempPassword) {
                const successResult = {
                  username: user.generatedUsername,
                  firstName: user.first_name,
                  lastName: user.last_name,
                  role: user.role,
                  site1: user.site1,
                  site2: user.site2,
                  site3: user.site3,
                  site4: user.site4,
                  site5: user.site5,
                  password: result.tempPassword,
                };
                results.push(successResult);
              } else {
                const errorResult = {
                  username: user.generatedUsername,
                  firstName: user.first_name,
                  lastName: user.last_name,
                  role: user.role,
                  site1: user.site1,
                  site2: user.site2,
                  site3: user.site3,
                  site4: user.site4,
                  site5: user.site5,
                  error: result.error || 'Erreur inconnue',
                };
                results.push(errorResult);
              }
            } catch (error) {
              const exceptionResult = {
                username: user.generatedUsername,
                firstName: user.first_name,
                lastName: user.last_name,
                role: user.role,
                site1: user.site1,
                site2: user.site2,
                site3: user.site3,
                site4: user.site4,
                site5: user.site5,
                error: 'Erreur lors de la création',
              };
              results.push(exceptionResult);
            }

            setImportProgress(prev => ({
              ...prev,
              current: prev.current + 1,
            }));
          })
        );
      }

      // Show results
      const successCount = results.filter(r => !r.error).length;
      const errorCount = results.filter(r => r.error).length;

      // Validation: Ensure we have results to display
      if (results.length === 0) {
        showToast('Erreur: Aucun résultat à afficher', 'error');
        setImporting(false);
        return;
      }

      // Set results BEFORE any other state updates
      setImportResults(results);
      setImporting(false);

      if (successCount > 0) {
        showToast(
          `${successCount} utilisateur${successCount > 1 ? 's' : ''} créé${successCount > 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} erreur${errorCount > 1 ? 's' : ''})` : ''}`,
          errorCount > 0 ? 'warning' : 'success'
        );
      } else {
        showToast('Aucun utilisateur n\'a pu être créé', 'error');
      }

      // DO NOT call onImportComplete() here - it will be called when the user closes the result modal
    } catch (error) {
      showToast('Erreur lors de l\'import', 'error');
      setImporting(false);
    }
  };

  const handleToggleSite = (siteName: string) => {
    setSelectedNewSites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteName)) {
        newSet.delete(siteName);
      } else {
        newSet.add(siteName);
      }
      return newSet;
    });
  };

  const getRoleLabel = (role: string) => {
    const roles: Record<string, string> = {
      operator: 'Opérateur',
      manager: 'Manager',
      director: 'Directeur',
      admin: 'Administrateur',
    };
    return roles[role] || role;
  };

  if (importResults) {
    return (
      <ImportResultModal
        results={importResults}
        newSites={Array.from(selectedNewSites)}
        onClose={() => {
          setImportResults(null);
          onClose();
        }}
        onImportComplete={onImportComplete}
      />
    );
  }

  return createPortal(
    <>
      <div
        className="modal-overlay"
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-modal-title"
      >
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
            <div>
              <h3 id="import-modal-title" className="text-xl font-bold text-gray-900">
                Importer des utilisateurs
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {step === 'upload' && 'Téléchargez un fichier CSV contenant les utilisateurs'}
                {step === 'preview' && `${validUsers.length} utilisateur${validUsers.length > 1 ? 's' : ''} prêt${validUsers.length > 1 ? 's' : ''} à importer`}
                {step === 'confirm-sites' && `Confirmer la création de ${selectedNewSites.size} nouveau${selectedNewSites.size > 1 ? 'x' : ''} site${selectedNewSites.size > 1 ? 's' : ''}`}
                {step === 'importing' && 'Import en cours...'}
              </p>
            </div>
            {!importing && (
              <button
                onClick={handleCloseAttempt}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {step === 'upload' && (
              <div className="space-y-6">
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Format du fichier CSV</h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>
                      <strong>Colonnes requises:</strong> first_name, last_name, role
                    </li>
                    <li>
                      <strong>Colonnes optionnelles:</strong> site1, site2, site3, site4, site5 (max 5 sites par utilisateur)
                    </li>
                    <li>
                      <strong>Séparateur:</strong> Détection automatique (virgule, point-virgule ou tabulation)
                    </li>
                    <li>
                      <strong>Rôles valides:</strong> operator, manager, director, admin
                    </li>
                    <li>
                      <strong>Sites:</strong> Les sites sont automatiquement ignorés pour les rôles admin et director
                    </li>
                    <li>
                      <strong>Nom d'utilisateur:</strong> Généré automatiquement (nom + 2 premières lettres du prénom)
                    </li>
                    <li>
                      <strong>Nouveaux sites:</strong> Les sites non existants seront créés automatiquement
                    </li>
                    <li>
                      <strong>Limite:</strong> Maximum 500 utilisateurs par fichier
                    </li>
                  </ul>
                </div>

                {/* Template Download */}
                <div className="flex justify-center">
                  <button
                    onClick={downloadCSVTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all duration-200"
                  >
                    <Download size={20} />
                    <span>Télécharger le modèle CSV</span>
                  </button>
                </div>

                {/* Drop Zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-primary-500 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    Glissez-déposez votre fichier CSV ici
                  </p>
                  <p className="text-sm text-gray-500 mb-4">ou cliquez pour parcourir</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  {csvFile && (
                    <p className="text-sm text-primary-600 font-medium">
                      Fichier sélectionné: {csvFile.name}
                    </p>
                  )}
                </div>

                {/* Parse Errors */}
                {parseErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <p className="font-semibold text-red-900 mb-2">Erreurs détectées</p>
                        <ul className="text-sm text-red-800 space-y-1">
                          {parseErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'preview' && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-600" size={24} />
                      <div>
                        <p className="text-sm text-green-800 font-medium">Utilisateurs valides</p>
                        <p className="text-2xl font-bold text-green-900">{validUsers.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <XCircle className="text-red-600" size={24} />
                      <div>
                        <p className="text-sm text-red-800 font-medium">Erreurs</p>
                        <p className="text-2xl font-bold text-red-900">{invalidUsers.length}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="text-blue-600" size={24} />
                      <div>
                        <p className="text-sm text-blue-800 font-medium">Nouveaux sites</p>
                        <p className="text-2xl font-bold text-blue-900">{newSites.length}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview Table */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Aperçu des données</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-96">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Ligne
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Nom d'utilisateur
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Prénom
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Nom
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Rôle
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Sites
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                              Statut
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {parsedUsers.map((user, index) => {
                            const roleIgnoresSites = user.role === 'admin' || user.role === 'director';
                            const userSites = [user.site1, user.site2, user.site3, user.site4, user.site5]
                              .filter(site => site && site.length > 0);
                            const sitesDisplay = roleIgnoresSites
                              ? '-'
                              : userSites.length > 0
                                ? userSites.join(', ')
                                : '-';

                            return (
                              <tr
                                key={index}
                                className={user.isValid ? 'bg-white hover:bg-gray-50' : 'bg-red-50'}
                              >
                                <td className="px-4 py-3 text-sm text-gray-700">{user.lineNumber}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                  {user.generatedUsername}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">{user.first_name}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{user.last_name}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {getRoleLabel(user.role)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {sitesDisplay}
                                  {roleIgnoresSites && userSites.length > 0 && (
                                    <span className="ml-2 text-xs text-gray-500 italic">
                                      (ignorés)
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  {user.isValid ? (
                                    <span className="flex items-center gap-1 text-green-700">
                                      <CheckCircle size={16} />
                                      <span>Valide</span>
                                    </span>
                                  ) : (
                                    <div className="text-red-700">
                                      <div className="flex items-center gap-1 mb-1">
                                        <XCircle size={16} />
                                        <span>Erreur</span>
                                      </div>
                                      <ul className="text-xs space-y-0.5">
                                        {user.validationErrors.map((error, i) => (
                                          <li key={i}>• {error}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 'confirm-sites' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900 mb-2">Nouveaux sites détectés</p>
                      <p className="text-sm text-blue-800">
                        Les sites suivants n'existent pas encore et seront créés automatiquement.
                        Vous pouvez décocher ceux que vous ne souhaitez pas créer.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="space-y-3">
                    {newSites.map((siteName, index) => (
                      <label
                        key={index}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedNewSites.has(siteName)}
                          onChange={() => handleToggleSite(siteName)}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-900">{siteName}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {selectedNewSites.size < newSites.length && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Attention:</strong> Les utilisateurs nécessitant les sites non
                      sélectionnés ne pourront pas être créés.
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 'importing' && (
              <div className="space-y-6">
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-600 mb-6" size={48} />
                  <p className="text-lg font-semibold text-gray-900 mb-4">
                    {importProgress.stage}
                  </p>
                  <div className="w-full max-w-md">
                    <ProgressBar
                      current={importProgress.current}
                      total={importProgress.total}
                      color="primary"
                      showRatio={true}
                      showPercentage={true}
                    />
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 text-center">
                    Veuillez ne pas fermer cette fenêtre pendant l'import...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!importing && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={handleCloseAttempt}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  Annuler
                </button>
                {step === 'preview' && (
                  <button
                    onClick={handleProceedToSiteConfirmation}
                    disabled={validUsers.length === 0}
                    className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continuer ({validUsers.length} utilisateur{validUsers.length > 1 ? 's' : ''})
                  </button>
                )}
                {step === 'confirm-sites' && (
                  <button
                    onClick={handleStartImport}
                    disabled={selectedNewSites.size === 0 && newSites.length > 0}
                    className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Lancer l'import
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showCloseConfirmation}
        title="Annuler l'import"
        message="Êtes-vous sûr de vouloir annuler l'import ? Les données saisies seront perdues."
        confirmLabel="Annuler l'import"
        cancelLabel="Continuer"
        onConfirm={onClose}
        onCancel={() => setShowCloseConfirmation(false)}
        variant="warning"
      />
    </>,
    getPortalRoot()
  );
}
